'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // 更新 state 使下一次渲染能够显示降级后的 UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // 保存详细错误信息到 state
    this.setState({
      errorInfo,
    });

    // 记录详细错误信息到控制台
    console.group('🔴 ErrorBoundary - Component Error');
    console.error('Error:', error);
    console.error('Error Name:', error.name);
    console.error('Error Message:', error.message);
    console.error('Error Stack:', error.stack);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();

    // 调用自定义错误处理函数
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  private copyErrorInfo = () => {
    const { error, errorInfo } = this.state;
    if (!error) return;

    const errorText = `
错误信息：
类型: ${error.name}
消息: ${error.message}
时间: ${new Date().toISOString()}

JavaScript 堆栈:
${error.stack || '无堆栈信息'}

React 组件堆栈:
${errorInfo?.componentStack || '无组件堆栈信息'}

页面: ${typeof window !== 'undefined' ? window.location.href : 'N/A'}
    `.trim();

    navigator.clipboard.writeText(errorText);
  };

  public render() {
    if (this.state.hasError) {
      // 如果提供了自定义 fallback UI，使用它
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo } = this.state;

      // 默认的详细错误 UI
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          padding: '20px',
          textAlign: 'center',
          maxWidth: '900px',
          margin: '0 auto',
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px',
          }}>
            ⚠️
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            marginBottom: '8px',
            color: '#ef4444',
          }}>
            组件出错了
          </h2>
          <p style={{
            color: '#6b7280',
            marginBottom: '24px',
            maxWidth: '500px',
          }}>
            抱歉，这个组件遇到了一个错误。下方显示了详细的错误信息。
          </p>

          {/* 错误基本信息 */}
          {error && (
            <div style={{
              width: '100%',
              maxWidth: '800px',
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'left',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#374151' }}>错误类型：</strong>
                <span style={{ marginLeft: '8px', color: '#dc2626', fontFamily: 'monospace' }}>
                  {error.name}
                </span>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#374151' }}>错误消息：</strong>
                <div style={{
                  marginTop: '8px',
                  padding: '12px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '4px',
                }}>
                  <p style={{
                    color: '#b91c1c',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    margin: 0,
                    wordBreak: 'break-word',
                  }}>
                    {error.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* JavaScript 堆栈跟踪 */}
          {error?.stack && (
            <details style={{
              width: '100%',
              maxWidth: '800px',
              marginBottom: '16px',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              textAlign: 'left',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <summary style={{
                cursor: 'pointer',
                fontWeight: 'bold',
                color: '#1f2937',
                marginBottom: '12px',
              }}>
                📍 JavaScript 堆栈跟踪
              </summary>
              <div style={{
                marginTop: '12px',
                backgroundColor: '#1f2937',
                color: '#f3f4f6',
                borderRadius: '4px',
                padding: '12px',
                overflowX: 'auto',
              }}>
                <pre style={{
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0,
                }}>
                  {error.stack}
                </pre>
              </div>
              <p style={{
                marginTop: '12px',
                fontSize: '13px',
                color: '#6b7280',
                margin: '12px 0 0 0',
              }}>
                💡 查看第一行可找到错误发生的具体文件和行号
              </p>
            </details>
          )}

          {/* React 组件堆栈 */}
          {errorInfo?.componentStack && (
            <details
              open={process.env.NODE_ENV === 'development'}
              style={{
                width: '100%',
                maxWidth: '800px',
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '8px',
                textAlign: 'left',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              <summary style={{
                cursor: 'pointer',
                fontWeight: 'bold',
                color: '#1f2937',
                marginBottom: '12px',
              }}>
                🔗 React 组件堆栈
              </summary>
              <div style={{
                marginTop: '12px',
                backgroundColor: '#eff6ff',
                color: '#1e3a8a',
                borderRadius: '4px',
                padding: '12px',
                overflowX: 'auto',
                border: '1px solid #bfdbfe',
              }}>
                <pre style={{
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0,
                }}>
                  {errorInfo.componentStack}
                </pre>
              </div>
              <p style={{
                marginTop: '12px',
                fontSize: '13px',
                color: '#6b7280',
                margin: '12px 0 0 0',
              }}>
                💡 显示了错误在 React 组件树中的传播路径
              </p>
            </details>
          )}

          {/* 调试提示 */}
          <div style={{
            width: '100%',
            maxWidth: '800px',
            backgroundColor: '#dbeafe',
            border: '1px solid #93c5fd',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
            textAlign: 'left',
          }}>
            <h3 style={{
              fontWeight: 'bold',
              color: '#1e3a8a',
              marginBottom: '12px',
              marginTop: 0,
              fontSize: '16px',
            }}>
              🔍 调试提示
            </h3>
            <ul style={{
              color: '#1e40af',
              fontSize: '14px',
              paddingLeft: '20px',
              margin: 0,
            }}>
              <li style={{ marginBottom: '8px' }}>
                检查上方的堆栈跟踪，找到错误发生的具体位置
              </li>
              <li style={{ marginBottom: '8px' }}>
                查看 React 组件堆栈，了解错误在哪个组件中触发
              </li>
              <li style={{ marginBottom: '8px' }}>
                打开浏览器控制台（F12）查看更多详细信息
              </li>
              <li>
                点击下方&ldquo;复制错误信息&rdquo;按钮获取完整错误报告
              </li>
            </ul>
          </div>

          {/* 操作按钮 */}
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: '10px 20px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
              }}
            >
              🔄 重试
            </button>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
              }}
            >
              🏠 返回首页
            </button>
            <button
              onClick={this.copyErrorInfo}
              style={{
                padding: '10px 20px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
              }}
            >
              📋 复制错误信息
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
